import { List, ListItem } from "@solomon-apps/shared/src/components/Data/List";
import { IndicationUnit } from "@solomon-apps/shared/src/components/Metrics/IndicationUnit";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { linkVariants } from "@solomon-apps/shared/src/components/ui/link";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useClientPrograms } from "@/hooks/useClientPrograms";

import {
    buildProgramDashboardModel,
    type ProgramDashboardItem
} from "./ProgramsDashboard/programDashboardModel";

const STATUS_STATE: Record<string, "success" | "pending" | "disabled"> = {
    Active: "success",
    Draft: "pending",
    Paused: "pending",
    Ended: "disabled"
};

function currency(value: number): string {
    return `$${value.toLocaleString("en-US", {
        maximumFractionDigits: value % 1 === 0 ? 0 : 2
    })}`;
}

function plural(
    count: number,
    singular: string,
    pluralValue = `${singular}s`
): string {
    return `${count.toLocaleString("en-US")} ${count === 1 ? singular : pluralValue}`;
}

export function ProgramsDashboardPage(): React.ReactElement {
    const { programs: programViews, isLoading, error } = useClientPrograms();
    const model = useMemo(
        () => buildProgramDashboardModel(programViews),
        [programViews]
    );
    const [selectedId, setSelectedId] = useState<string>("");

    useEffect(() => {
        if (model.programs.some((program) => program.id === selectedId)) return;
        setSelectedId(model.programs[0]?.id ?? "");
    }, [model.programs, selectedId]);

    const selected =
        model.programs.find((program) => program.id === selectedId) ??
        model.programs[0] ??
        null;

    return (
        <div>
            <section className="py-6 border-b border-line-base">
                <Heading title="Programs Dashboard" className="px-6" />
            </section>

            <section className="border-b border-line-base">
                <div className="grid grid-cols-1 sm:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-line-base">
                    <StatCell
                        label="Active Programs"
                        value={model.totals.programCount.toLocaleString(
                            "en-US"
                        )}
                    />
                    <StatCell
                        label="Total USDv Held"
                        value={currency(model.totals.totalHeld)}
                    />
                    <StatCell
                        label="Total USDv Rewarded"
                        value={currency(model.totals.totalRewards)}
                    />
                    <StatCell
                        label="Users Holding USDv"
                        value={model.totals.userCount.toLocaleString("en-US")}
                    />
                </div>
            </section>

            {error ? (
                <DashboardMessage
                    title="Programs could not be loaded."
                    body="Refresh the page or try again after the Program API is available."
                />
            ) : isLoading && model.programs.length === 0 ? (
                <DashboardMessage title="Loading programs." />
            ) : model.programs.length === 0 ? (
                <DashboardMessage
                    title="No active user programs."
                    body="Create or activate a company recipient-run user program before reviewing program-level users and rewards."
                    action={
                        <Text
                            variant="small"
                            render={<Link to="/programs" />}
                            className="px-5 py-2.5 bg-surface-inverse-raised text-foreground-inverse hover:bg-accent-base transition-colors duration-200"
                        >
                            View programs
                        </Text>
                    }
                />
            ) : (
                <div className="flex flex-col lg:flex-row">
                    <aside className="lg:w-80 lg:shrink-0 border-b lg:border-b-0 lg:border-r border-line-base pb-8">
                        <div className="px-4 py-5">
                            <Text variant="h2" className="text-foreground-base">
                                Active Programs
                            </Text>
                        </div>
                        <List
                            selectable="single"
                            selected={selected?.id ?? ""}
                            onSelect={setSelectedId}
                        >
                            {model.programs.map((program) => (
                                <ProgramRow
                                    key={program.id}
                                    program={program}
                                />
                            ))}
                        </List>
                    </aside>

                    <section className="flex-1 min-w-0 py-8">
                        {selected ? <ProgramDetail program={selected} /> : null}
                    </section>
                </div>
            )}
        </div>
    );
}

interface StatCellProps {
    label: string;
    value: string;
}

function StatCell({ label, value }: StatCellProps): React.ReactElement {
    return (
        <div className="px-6 py-5">
            <Text
                variant="fine-print"
                render={<span />}
                className="text-foreground-muted block"
            >
                {label}
            </Text>
            <Text
                variant="h2"
                render={<span />}
                className="text-foreground-base block mt-2"
            >
                {value}
            </Text>
        </div>
    );
}

interface DashboardMessageProps {
    title: string;
    body?: string;
    action?: React.ReactNode;
}

function DashboardMessage({
    title,
    body,
    action
}: DashboardMessageProps): React.ReactElement {
    return (
        <section className="px-6 py-10 max-w-[720px]">
            <Text variant="h2" className="text-foreground-base">
                {title}
            </Text>
            {body ? (
                <Text variant="body" className="text-foreground-muted mt-2">
                    {body}
                </Text>
            ) : null}
            {action ? <div className="mt-6">{action}</div> : null}
        </section>
    );
}

interface ProgramRowProps {
    program: ProgramDashboardItem;
}

function ProgramRow({ program }: ProgramRowProps): React.ReactElement {
    return (
        <ListItem itemKey={program.id} padding="px-4 py-3" gap="gap-0">
            <div className="min-w-0">
                <div className="flex items-center gap-2">
                    <IndicationUnit
                        variant="status"
                        state={STATUS_STATE[program.status] ?? "pending"}
                        size={7}
                    />
                    <Text
                        variant="small"
                        render={<span />}
                        className="text-foreground-base block truncate"
                    >
                        {program.name}
                    </Text>
                </div>
                <Text
                    variant="fine-print"
                    className="text-foreground-muted mt-1 pl-4"
                >
                    {plural(program.userCount, "user")} /{" "}
                    {plural(program.positionCount, "position")}
                </Text>
            </div>
        </ListItem>
    );
}

function ProgramDetail({
    program
}: {
    program: ProgramDashboardItem;
}): React.ReactElement {
    return (
        <>
            <div className="px-6 flex flex-wrap items-center justify-between gap-x-8 gap-y-2">
                <div className="flex flex-wrap items-center gap-x-8 gap-y-1">
                    <Text
                        variant="h2"
                        render={
                            <Link
                                to={`/programs?id=${program.id}`}
                                className={`${linkVariants()} text-foreground-base inline-block w-fit decoration-[1.5px]`}
                            />
                        }
                    >
                        {program.name}
                    </Text>
                    <HeaderField
                        label="Surfaces"
                        value={program.surfaceCount.toLocaleString("en-US")}
                    />
                    <HeaderField
                        label="Positions"
                        value={program.positionCount.toLocaleString("en-US")}
                    />
                </div>
                <HeaderField label="Status" value={program.status} />
            </div>

            {program.description ? (
                <Text
                    variant="body"
                    className="px-6 mt-3 max-w-[720px] text-foreground-muted"
                >
                    {program.description}
                </Text>
            ) : null}

            <div className="mt-4 border-t border-b border-line-base grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-line-base">
                <MiniStat
                    label="USDv Held"
                    value={currency(program.totalHeld)}
                />
                <MiniStat
                    label="USDv Rewarded"
                    value={currency(program.totalRewards)}
                />
                <MiniStat
                    label="Users Holding USDv"
                    value={program.userCount.toLocaleString("en-US")}
                />
            </div>
        </>
    );
}

interface MiniStatProps {
    label: string;
    value: string;
}

function MiniStat({ label, value }: MiniStatProps): React.ReactElement {
    return (
        <div className="px-6 py-4 flex flex-col">
            <Text
                variant="fine-print"
                render={<span />}
                className="text-foreground-muted"
            >
                {label}
            </Text>
            <Text
                variant="body"
                render={<span />}
                className="text-foreground-base font-bold mt-0.5"
            >
                {value}
            </Text>
        </div>
    );
}

interface HeaderFieldProps {
    label: string;
    value: string;
}

function HeaderField({ label, value }: HeaderFieldProps): React.ReactElement {
    return (
        <Text
            variant="small"
            render={<span />}
            className="text-foreground-muted whitespace-nowrap"
        >
            {label}:{" "}
            <span className="text-foreground-base font-numeric">{value}</span>
        </Text>
    );
}
